import { useEffect } from "react";
import { useAtomValue } from "jotai";
import { userHasPro } from "@/utilities/pro";
import { MAX_FREE_AI_SUGGESTIONS } from "@/config/constants";
import { useQueryClient } from "@tanstack/react-query";
import { isLoggingOutAtom } from "@/state/dialogs";
import { useSession } from "@/hooks/auth-hooks";
import type { User } from "instantdb/types";

type useUserReturnType = {
  user?: User;
  userId?: string;
  language?: string;
  isAuthenticated: boolean;
  /**
   * Whether it is determined if the user is logged in or not
   */
  enabled: boolean;
  hasPro: boolean;
  hasFreeAISuggestions: boolean;
  refetchUser: () => void;
};

export const useUser = (): useUserReturnType => {
  const { data: session, isPending, refetch } = useSession();
  const user = session?.user;
  const userId = user?.id;

  const isLoggingOut = useAtomValue(isLoggingOutAtom);
  const queryClient = useQueryClient();

  const isAuthenticated = !isLoggingOut && Boolean(session?.user);

  // Reset queries only when changing to unauthenticated state from authenticated state
  useEffect(() => {
    if (!session?.user) {
      queryClient.removeQueries({ queryKey: ["user"] });
    }
  }, [session?.user, queryClient]);

  const enabled = Boolean(session?.user) || !isPending;

  const hasPro = userHasPro(user);
  const hasFreeAISuggestions = Boolean(
    isAuthenticated && (hasPro || (user?.aiSuggestions ?? 0) < MAX_FREE_AI_SUGGESTIONS),
  );

  return {
    user,
    userId,
    language: user?.language ?? "en",
    aiLanguage: user?.aiLanguage,
    isAuthenticated,
    enabled,
    hasPro,
    hasFreeAISuggestions,
    refetchUser: refetch,
  };
};
